/**
 * HTTP Client
 *
 * Axios-based HTTP client with preconfigured interceptors.
 * This is the main entry point for making API requests.
 */

import axios, { type AxiosInstance } from "axios";
import { API_CONFIG } from "@/config/api";
import {
    authInterceptor,
    commonHeadersInterceptor,
    loggingInterceptor,
    successInterceptor,
    errorInterceptor,
} from "./interceptors";

// ============================================================================
// Configuration
// ============================================================================

/**
 * Determine the base URL based on environment
 * - In development: Use relative URL (proxied through Vite to avoid CORS)
 * - In production: Use the full backend URL
 */
const getBaseURL = (): string => {
    // Allow override via environment variable
    if (import.meta.env.VITE_API_BASE_URL) {
        return import.meta.env.VITE_API_BASE_URL;
    }

    return import.meta.env.DEV
        ? API_CONFIG.DEVELOPMENT_PATH
        : API_CONFIG.PRODUCTION_URL;
};

// ============================================================================
// HTTP Client Instance
// ============================================================================

/**
 * Create and configure the Axios instance
 */
const createHttpClient = (): AxiosInstance => {
    const instance = axios.create({
        baseURL: getBaseURL(),
        timeout: API_CONFIG.TIMEOUT,
        // Note: withCredentials is NOT needed since we use Bearer tokens (localStorage)
        // Setting it to true requires backend CORS to explicitly allow credentials
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
        },
    });

    // Request interceptors (run in order added)
    instance.interceptors.request.use(loggingInterceptor);
    instance.interceptors.request.use(authInterceptor);
    instance.interceptors.request.use(commonHeadersInterceptor);

    // Response interceptors
    instance.interceptors.response.use(successInterceptor, errorInterceptor);

    return instance;
};

/**
 * Configured HTTP client instance
 * Use this for all API requests
 */
export const httpClient = createHttpClient();

// ============================================================================
// Convenience Methods
// ============================================================================

/**
 * HTTP client with typed response methods
 */
export const http = {
    /**
     * GET request
     */
    async get<TResponse>(url: string, config?: Parameters<typeof httpClient.get>[1]) {
        const response = await httpClient.get<TResponse>(url, config);
        return response.data;
    },

    /**
     * POST request
     */
    async post<TResponse, TData = unknown>(
        url: string,
        data?: TData,
        config?: Parameters<typeof httpClient.post>[2]
    ) {
        const response = await httpClient.post<TResponse>(url, data, config);
        return response.data;
    },

    /**
     * PUT request
     */
    async put<TResponse, TData = unknown>(
        url: string,
        data?: TData,
        config?: Parameters<typeof httpClient.put>[2]
    ) {
        const response = await httpClient.put<TResponse>(url, data, config);
        return response.data;
    },

    /**
     * PATCH request
     */
    async patch<TResponse, TData = unknown>(
        url: string,
        data?: TData,
        config?: Parameters<typeof httpClient.patch>[2]
    ) {
        const response = await httpClient.patch<TResponse>(url, data, config);
        return response.data;
    },

    /**
     * DELETE request
     */
    async delete<TResponse>(url: string, config?: Parameters<typeof httpClient.delete>[1]) {
        const response = await httpClient.delete<TResponse>(url, config);
        return response.data;
    },
};

export default httpClient;
